import React, { useEffect } from 'react';
import moment from 'moment';
import * as routes from '../../routes';
import { processDecisionSupportSubpoints } from '../../actions/decisionSupportActions';
import { startAvailabilitySearch } from '../../actions/workflowActions';
import GuidedResponseComponent from '../../components/guidedResponse/guidedResponseComponent';
import { useAppDispatch, useAppSelector } from '../../store';

const DecisionSupportView: React.FC<any> = (props) => {
	const dispatch = useAppDispatch();

	const activeCareOrder = useAppSelector((state) => state.careOrder);
	const auth = useAppSelector((state) => state.auth);
	const sessionBookingContext = useAppSelector((state) => state.session.bookingContext);
	const config = useAppSelector((state) => state.config);
	const correlationKey = useAppSelector((state) => state.session.correlationKey);
	const decisionSupportConfig = useAppSelector((state) => state.config.decisionSupport);
	const isLoading = useAppSelector((state) => state.ajaxCallsInProgress > 0);
	const patient = useAppSelector((state) => state.patient.patient);
	const tokenLink = useAppSelector((state) => state.session.tokenLink);

	const handleGuidedResponseFlowComplete = (results: any) => {
		dispatch(processDecisionSupportSubpoints(results, true)).then((data: any) => {
			dispatch(
				startAvailabilitySearch(
					data.activeCareOrder,
					data.decisionSupportOutput,
					data.correlationKey,
					data.availabilitySearchConfig,
					data.patientDetails,
				),
			)
			.then(() => {
				dispatch(routes.searchAvailability(config.instance.routePrefix));
			});
		});
	};

	const buildGuidedResponseInputData = (patient: any, activeCareOrderDetails: any, bookingContext: any, tokenLink: any) => {
		var fields: any[] = [];

		mapBookingContextToGuidedResponseInputFields(fields, bookingContext);
		mapPatientToGuidedResponseInputFields(fields, patient);
		mapCareOrderToGuidedResponseInputFields(fields, activeCareOrderDetails);

		if (tokenLink.inputValueType && tokenLink.inputName && tokenLink.inputValue)
			mapTokenLinkToGuidedResponseInputs(fields, tokenLink);

		return { inputFields: fields };
	};

	const mapBookingContextToGuidedResponseInputFields = (fields: any, bookingContext: any) => {
		if (bookingContext) {
			addOrOverrideFieldIfPresent(fields, 'Npi', 'String', bookingContext.npi);
			addOrOverrideFieldIfPresent(fields, 'SiteId', 'String', bookingContext.siteId);
			addOrOverrideFieldIfPresent(fields, 'SpecialtyId', 'String', bookingContext.specialtyId);
			addOrOverrideFieldIfPresent(fields, 'specialtyName', 'String', bookingContext.specialtyName);
		}
	};

	const mapPatientToGuidedResponseInputFields = (fields: any[], patient: any) => {
		if (patient) {
			addOrOverrideFieldIfPresent(fields, 'PatientReferenceId', 'String', patient.referenceId);
			addOrOverrideFieldIfPresent(fields, 'PatientMemberId', 'String', patient.memberId);
			addOrOverrideFieldIfPresent(fields, 'PatientGroupNumber', 'String', patient.groupNumber);
			addOrOverrideFieldIfPresent(fields, 'PatientFirstName', 'String', patient.firstName);
			addOrOverrideFieldIfPresent(fields, 'PatientMiddleName', 'String', patient.middleName);
			addOrOverrideFieldIfPresent(fields, 'PatientLastName', 'String', patient.lastName);
			addOrOverrideFieldIfPresent(fields, 'PatientDateOfBirth', 'Date', getDateString(patient.dateOfBirth));
			addOrOverrideFieldIfPresent(fields, 'PatientGenderCode', 'String', patient.gender);
			addOrOverrideFieldIfPresent(fields, 'PatientGuardianName', 'String', patient.guardianName);
			addOrOverrideFieldIfPresent(fields, 'PatientEmail', 'String', patient.email);
			addOrOverrideFieldIfPresent(fields, 'PatientHomePhone', 'String', patient.homePhone);
			addOrOverrideFieldIfPresent(fields, 'PatientMobilePhone', 'String', patient.mobilePhone);
			addOrOverrideFieldIfPresent(fields, 'PatientAlternatePhone', 'String', patient.alternatePhone);
			addOrOverrideFieldIfPresent(fields, 'PatientAddressLine1', 'String', patient.addressLine1);
			addOrOverrideFieldIfPresent(fields, 'PatientAddressLine2', 'String', patient.addressLine2);
			addOrOverrideFieldIfPresent(fields, 'PatientCityName', 'String', patient.cityName);
			addOrOverrideFieldIfPresent(fields, 'PatientStateCode', 'String', patient.stateCode);
			addOrOverrideFieldIfPresent(fields, 'PatientZipCode', 'String', patient.zipCode);
			addOrOverrideFieldIfPresent(fields, 'PatientLatitude', 'String', patient.latitude);
			addOrOverrideFieldIfPresent(fields, 'PatientLongitude', 'String', patient.longitude);
			addOrOverrideFieldIfPresent(fields, 'PatientPoolPrefix', 'String', patient.patientPoolPrefix);
		}
	};

	const mapCareOrderToGuidedResponseInputFields = (fields: any[], activeCareOrderDetails: any) => {
		if (activeCareOrderDetails) {
			if (!fields.find((f) => f.fieldName === 'PatientZipCode')) {
				addOrOverrideFieldIfPresent(fields, 'PatientZipCode', 'String', activeCareOrderDetails.patientZip);
			}

			addOrOverrideFieldIfPresent(
				fields,
				'MaxVisitDate',
				'Date',
				getDateString(activeCareOrderDetails.maxVisitDate),
			);
			addOrOverrideFieldIfPresent(
				fields,
				'MinVisitDate',
				'Date',
				getDateString(activeCareOrderDetails.minVisitDate),
			);
			addOrOverrideFieldIfPresent(
				fields,
				'OrderExpirationDate',
				'Date',
				getDateString(activeCareOrderDetails.expirationDate),
			);

			if (activeCareOrderDetails.appointments && activeCareOrderDetails.appointments.length === 1) {
				let appointment = activeCareOrderDetails.appointments[0];

				addOrOverrideFieldIfPresent(fields, 'AppointmentTypeId', 'String', appointment.appointmentTypeId);
				addOrOverrideFieldIfPresent(fields, 'AppointmentTypeName', 'String', appointment.appointmentTypeName);

				//currently NPI isn't passed via careorders/appt BUT if it ever is then we have a hook for it.
				addOrOverrideFieldIfPresent(fields, 'NPI', 'String', appointment.npi);
				addOrOverrideFieldIfPresent(fields, 'ServiceTypeId', 'String', appointment.serviceTypeId);
				addOrOverrideFieldIfPresent(fields, 'ServiceId', 'String', appointment.serviceId);
				addOrOverrideFieldIfPresent(fields, 'SpecialtyId', 'String', appointment.specialtyId);
				addOrOverrideFieldIfPresent(fields, 'SiteId', 'String', appointment.siteId);
				addOrOverrideFieldIfPresent(
					fields,
					'ExternalReferralOrderNumber',
					'String',
					appointment.externalReferralOrderId,
				);

				if (appointment.careOrderCustomField !== null && appointment.careOrderCustomField.length) {
					appointment.careOrderCustomField.forEach((x: any) => {
						addOrOverrideFieldIfPresent(fields, `${x.fieldName}`, 'String', `${x.fieldValue}`);
					});
				}
			}
		}
	};

	const mapTokenLinkToGuidedResponseInputs = (fields: any[], tokenLink: any) => {
		switch (tokenLink.inputValueType.toLowerCase()) {
			case 'date':
				addOrOverrideFieldIfPresent(fields, tokenLink.inputName, tokenLink.inputValueType, getDateString(tokenLink.inputValue));
				break;
			case 'string':
			default:
				addOrOverrideFieldIfPresent(fields, tokenLink.inputName, tokenLink.inputValueType, tokenLink.inputValue);
				break;
		}
	};

	const getDateString = (date: string | Date) => {
		if (date !== null && date !== undefined) {
			const d = moment(date).format('YYYY-MM-DD');
			return d;
		}
	};

	//TODO: move addFieldIfPresent and createField to a GuidedResponseHelpers file?
	const addOrOverrideFieldIfPresent = (fields: any[], fieldName: string, valueType: string, currentValue: any) => {
		// Note: The "if present" refers the presence of a currentValue to assign, not a field in the list
		if (currentValue) {
			let field = fields.find((f) => f.fieldName.toLowerCase() === fieldName.toLowerCase());
			if (field === undefined) {
				field = createField(fieldName, valueType, currentValue);
				fields.push(field);
			} else {
				// Code assumes value types agree, else we have a bug
				field.currentValue = currentValue;
			}
		}
	};

	const createField = (fieldName: string, valueType: string, currentValue: any) => {
		var field = {
			fieldName: fieldName,
			valueType: valueType,
			currentValue: currentValue,
		};
		return field;
	};

	useEffect(() => {
		if (!decisionSupportConfig.useDecisionSupport) {
			dispatch(routes.home(config.instance.routePrefix));
		}
	}, []);

	const inputData = buildGuidedResponseInputData(
		patient,
		activeCareOrder.activeCareOrderDetails,
		sessionBookingContext,
		tokenLink,
	);

	return (
		<>
			<GuidedResponseComponent
				contextData={inputData}
				correlationKey={correlationKey}
				decisionSupportConfig={decisionSupportConfig}
				isLoading={isLoading}
				onCompleted={handleGuidedResponseFlowComplete}
				productInstanceId={auth.productInstanceId}
				patientReferenceId={patient?.referenceId}
			/>
		</>
	);
};
export default DecisionSupportView;
